/**
 * Shared model for the Scenarios page (`features/sku-velocity/scenarios/`).
 *
 * Pure, no DOM — composes the existing capacity primitives in `math.ts` (`capNumbers`,
 * `heldAt`, `acceptedUnion`, `leverAccepted`, `setRoll`, `cumAt`, `firstBreachYear`,
 * `RELO_STOPS`, `CAP_YEARS`) with the real snapshot (`Sku[]`, `Site`, `CapacityModel`,
 * `LeverFamily[]`) to answer the one question the screen exists for: does what the site holds
 * fit the ceiling, and what does closing the gap cost.
 *
 * The five levers on the new Scenarios page map onto the existing model almost 1:1:
 *   - relocate (p1)  → the `relo`  space lever, threshold dial snapped onto `RELO_STOPS`
 *   - pack (p2)      → the `pack`  space lever, TOP-N families by `base` kept
 *   - convert (p3)   → the `conv`  space lever, TOP-N fitting sets by `base` kept
 *   - capacity (p4)  → NOT the old `cap` lever (that adds a flat `UNIT_SKU` regardless of
 *                       system) — see `runScenario`, which manages the ceiling itself off the
 *                       three named systems in `scenarios/mock.ts`.
 *   - pace (p5)      → wholly new: defers a share of the 2027 NPI intake into 2028/29.
 *
 * Priority order is relocate, then pack, then convert, so a SKU eligible for more than one
 * lever is claimed exactly once — `buildClaimMap` is the SINGLE source every other figure here
 * (per-lever deltas, band chips, the scatter's dimming) reads, so they can never disagree.
 */

// Imports directly from `./math` / `./types`, never the `./index` barrel: this module IS one
// of the barrel's re-exports, and importing the barrel back would be a cycle.
import {
  CAP_YEARS,
  capNumbers,
  defaultRoll,
  heldAt,
  leverAccepted,
  leverKept,
  RELO_STOPS,
} from "./math";
import type { Band, CapacityModel, LeverFamily, RollPlan, Site, Sku, SpaceLeverId } from "./types";

/** Lever ids on the Scenarios page — the wire's `agent_set` names these, not `LeverId`
 *  (which has no "pace" and spells the capital lever "cap" for a different, older model). */
export type Scenario5LeverId = "relo" | "pack" | "conv" | "cap" | "pace";

/** Which of `Sku.chan` / `Sku.mon` etc a SKU carries — the cause a lever claimed it for, if
 *  any. `null` = untouched by every engaged lever. */
export type LeverCause = "relo" | "pack" | "conv" | null;

/**
 * The five-lever draft, camelCase — the TS-side shape `runScenario` reads. `scenarios/state.ts`
 * owns the reducer over this; `scenarios/stream.ts` maps it to/from the locked snake_case wire
 * shape (`relo_threshold`, `pack_count`, …) for the agent SSE endpoint.
 */
export interface ScenarioParams {
  /** The capacity-table row the levers are being worked against. */
  year: number;
  /** -1 = Off (distinct from 0, itself a selection: all dead stock). Otherwise 0–24 picks/yr. */
  reloThreshold: number;
  /** Advanced override: families the threshold would include, manually dropped. */
  reloExcluded: string[];
  /** Pack families selected, 0 (neutral) to 10 — the TOP N by `LeverFamily.base`. */
  packCount: number;
  packExcluded: string[];
  /** Fitting sets selected, 0 (neutral) to 8. */
  convCount: number;
  convExcluded: string[];
  /** `null` = no system chosen. Sticky: turning the lever off is `capUnits = 0`, not clearing
   *  this back to `null` (mirrors the wire's `cap_system`). */
  capSystem: "aframe" | "p2l" | "perfect" | null;
  /** Automation units on `capSystem`, 0 (neutral) to 6. */
  capUnits: number;
  /** % of the 2027 NPI intake deferred half into 2028, half into 2029. 0 = neutral. */
  pacePct: number;
  /** Levers the agent last set — cleared for a lever the moment the user moves it manually. */
  agentSet: Scenario5LeverId[];
}

export function neutralScenarioParams(year: number): ScenarioParams {
  return {
    year,
    reloThreshold: -1,
    reloExcluded: [],
    packCount: 0,
    packExcluded: [],
    convCount: 0,
    convExcluded: [],
    capSystem: null,
    capUnits: 0,
    pacePct: 0,
    agentSet: [],
  };
}

/** Two `ScenarioParams` are the same CASE when every dial matches — `agentSet` doesn't count
 *  (an agent-applied setting and the same setting moved by hand are the same scenario). */
export function sameParams(a: ScenarioParams, b: ScenarioParams): boolean {
  return (
    a.reloThreshold === b.reloThreshold &&
    sameIds(a.reloExcluded, b.reloExcluded) &&
    a.packCount === b.packCount &&
    sameIds(a.packExcluded, b.packExcluded) &&
    a.convCount === b.convCount &&
    sameIds(a.convExcluded, b.convExcluded) &&
    a.capSystem === b.capSystem &&
    a.capUnits === b.capUnits &&
    a.pacePct === b.pacePct
  );
}
function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

/** At least one lever engaged past its neutral value. */
export function engaged(p: ScenarioParams): boolean {
  return (
    p.reloThreshold >= 0 ||
    p.packCount > 0 ||
    p.convCount > 0 ||
    (!!p.capSystem && p.capUnits > 0) ||
    p.pacePct > 0
  );
}

export interface ScenarioContext {
  skus: readonly Sku[];
  site: Site;
  model: CapacityModel;
  families: Record<SpaceLeverId, readonly LeverFamily[]>;
  /**
   * Faces added per automation unit, as a FRACTION of the real ceiling, keyed by system — owned
   * by `scenarios/mock.ts` (the isolated-placeholder module for the three named systems' unit
   * economics) and threaded in here so this file stays free of any feature-level import. A
   * fraction, not an absolute count, so an edited ceiling still reconciles (plan decision).
   */
  systemAddFacesFraction: Record<NonNullable<ScenarioParams["capSystem"]>, number>;
}

/** Real per-band SKU counts — the home-band sizes `assignPlacements` (mock.ts) fills each system
 *  up to, and the denominator every band-fraction figure on the screen divides by. */
export function realBandCounts(skus: readonly Sku[]): Record<Band, number> {
  const out: Record<Band, number> = { A: 0, B: 0, C: 0, D: 0 };
  for (const s of skus) {
    out[s.band]++;
  }
  return out;
}

/** The velocity-threshold dial (0–24 picks/yr) snapped onto the nearest real `RELO_STOPS`
 *  index — the stop whose `mv` sits closest to the dial. Mirrors the backend's
 *  `relo_stop_for_threshold` so a chat-set threshold and a slider-set one land on the same
 *  stop. `-1` (Off) is never passed in; callers guard `reloThreshold >= 0` first. */
export function reloStopForThreshold(threshold: number): number {
  let best = 0;
  let bestDiff = Infinity;
  RELO_STOPS.forEach((stop, i) => {
    const diff = Math.abs(stop.mv - threshold);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  });
  return best;
}

/** The TOP `count` families by `base` (release value), then any manually dropped id removed —
 *  the pack/convert dial's "N of however-many" selects the biggest movers first, same as the
 *  prototype's `PACK_REL`/`CONV_REL` tables ordered so the suggested plan sums to the canon.
 *
 *  Exclusions are removals ON TOP of the top-N, NOT a filter applied before it — so dropping a
 *  selected family does NOT backfill the next one in. This mirrors the backend exactly, where the
 *  removed set is `top_n_ids(count) ∪ excluded` (`capacity_tools.py`); backfilling here would make
 *  the live dashboard numbers disagree with the agent/tool math for the same scenario. */
export function familiesKeptForCount(
  families: readonly LeverFamily[],
  count: number,
  excludedIds: readonly string[],
): LeverFamily[] {
  const topN = families
    .slice()
    .sort((a, b) => b.base - a.base)
    .slice(0, Math.max(0, count));
  return topN.filter((f) => !excludedIds.includes(f.id));
}

/** The complement of `familiesKeptForCount` — the ids `acceptedUnion`/`leverAccepted` should
 *  treat as removed, i.e. every family NOT in the kept top-N. */
function removedForCount(
  families: readonly LeverFamily[],
  count: number,
  excludedIds: readonly string[],
): string[] {
  const kept = new Set(familiesKeptForCount(families, count, excludedIds).map((f) => f.id));
  return families.filter((f) => !kept.has(f.id)).map((f) => f.id);
}

/** `removedFam` for `capNumbers`/`acceptedUnion` — one list per space lever, reusable by both
 *  `runScenario` (the totals) and `buildClaimMap` (the per-SKU attribution), so the two can
 *  never drift off different candidate sets. */
export function removedFamFor(
  params: ScenarioParams,
  ctx: ScenarioContext,
): Record<SpaceLeverId, string[]> {
  return {
    relo: [...params.reloExcluded],
    pack: removedForCount(ctx.families.pack, params.packCount, params.packExcluded),
    conv: removedForCount(ctx.families.conv, params.convCount, params.convExcluded),
  };
}

/** Space levers currently engaged (capacity/pace are handled outside `SPACE_LEVERS`). */
export function activeSpaceLevers(params: ScenarioParams): SpaceLeverId[] {
  const active: SpaceLeverId[] = [];
  if (params.reloThreshold >= 0) {
    active.push("relo");
  }
  if (params.packCount > 0) {
    active.push("pack");
  }
  if (params.convCount > 0) {
    active.push("conv");
  }
  return active;
}

/**
 * Which lever (if any) claims each real SKU — relocate, then pack, then convert, so no SKU is
 * counted twice. The SAME `memberIds`/`memberStop` sets `acceptedUnion` unions for the totals,
 * walked here per-lever so the scatter's dimming, the band chips and the lever-card deltas are
 * one fact instead of three approximations of it.
 */
export function buildClaimMap(
  params: ScenarioParams,
  ctx: ScenarioContext,
): Map<number, LeverCause> {
  const claim = new Map<number, LeverCause>();
  const reloStop = params.reloThreshold >= 0 ? reloStopForThreshold(params.reloThreshold) : -1;
  const removedFam = removedFamFor(params, ctx);
  const order: { id: SpaceLeverId; active: boolean }[] = [
    { id: "relo", active: params.reloThreshold >= 0 },
    { id: "pack", active: params.packCount > 0 },
    { id: "conv", active: params.convCount > 0 },
  ];
  for (const lever of order) {
    if (!lever.active) {
      continue;
    }
    const kept = leverKept(ctx.families[lever.id] ?? [], removedFam[lever.id]);
    let claimedAny = false;
    for (const f of kept) {
      if (!f.memberIds) {
        continue;
      }
      claimedAny = true;
      f.memberIds.forEach((skuId, i) => {
        if (claim.has(skuId)) {
          return;
        } // priority: first lever to claim a SKU wins
        const stop = f.memberStop?.[i];
        if (lever.id === "relo" && stop !== undefined && stop > reloStop) {
          return;
        }
        claim.set(skuId, lever.id);
      });
    }
    // Fallback for a drop old enough to serve no `memberIds` at all (e.g. the demo fixtures) —
    // without this, `capNumbers`' own `leverAccepted` fallback (see `math.ts`) would still move
    // the held/ceiling numbers while this claim map stayed empty, a direct on-screen
    // contradiction (levers reading "0 released" next to a capacity table that moved anyway).
    // Deterministic, illustrative selection only — real drops never take this path.
    if (!claimedAny && kept.length > 0) {
      const n = leverAccepted(kept, [], reloStop);
      fallbackClaim(lever.id, n, claim, ctx.skus);
    }
  }
  return claim;
}

/** Deterministic stand-in for `memberIds`-less families — claims the first `n` unclaimed SKUs
 *  from the band(s) the lever targets (D→C→B for relocate, B/C for pack, A for convert). */
function fallbackClaim(
  leverId: SpaceLeverId,
  n: number,
  claim: Map<number, LeverCause>,
  skus: readonly Sku[],
): void {
  if (n <= 0) {
    return;
  }
  const bandOrder: Band[] =
    leverId === "relo" ? ["D", "C", "B"] : leverId === "pack" ? ["B", "C"] : ["A"];
  let remaining = n;
  for (const band of bandOrder) {
    if (remaining <= 0) {
      break;
    }
    const pool = skus
      .filter((s) => s.band === band && !claim.has(s.id))
      .sort((a, b) => a.id - b.id);
    for (const s of pool) {
      if (remaining <= 0) {
        break;
      }
      claim.set(s.id, leverId as LeverCause);
      remaining--;
    }
  }
}

/** Per-band tally of what each lever claimed — the band chips' "removed" figure and the Pick
 *  Faces section's band-proportional reduction both read this, so they agree with the scatter. */
export function bandClaimBreakdown(
  claim: ReadonlyMap<number, LeverCause>,
  skus: readonly Sku[],
): Record<Band, { relo: number; pack: number; conv: number }> {
  const out: Record<Band, { relo: number; pack: number; conv: number }> = {
    A: { relo: 0, pack: 0, conv: 0 },
    B: { relo: 0, pack: 0, conv: 0 },
    C: { relo: 0, pack: 0, conv: 0 },
    D: { relo: 0, pack: 0, conv: 0 },
  };
  for (const s of skus) {
    const cause = claim.get(s.id);
    if (cause) {
      out[s.band][cause]++;
    }
  }
  return out;
}

/* ===== pacing (p5) — real roll plan, mock control surface ===== */

/** Only 2027 carries a launch in the reconciled canon, but this reads whichever family the
 *  live drop marks as the pinch year rather than assuming it. */
function paceableFamilies(model: CapacityModel) {
  const pinch = model.years?.[1] ?? 2027;
  return { pinch, families: model.npiFamilies.filter((f) => f.rel === pinch) };
}

/**
 * Builds the `RollPlan` `pacePct`% pacing implies: that share of the pinch year's family
 * releases moves out half into the next year, half into the year after — deferring demand,
 * never deleting it (an earlier build got the sign wrong; see build handover §5).
 */
export function applyDefer(pacePct: number, model: CapacityModel): RollPlan {
  const roll = defaultRoll(model.npiFamilies);
  const pct = Math.max(0, Math.min(100, pacePct)) / 100;
  const { pinch, families } = paceableFamilies(model);
  if (pct <= 0) {
    return roll;
  }
  const next = CAP_YEARS[CAP_YEARS.indexOf(pinch) + 1];
  const nextNext = CAP_YEARS[CAP_YEARS.indexOf(pinch) + 2];
  for (const f of families) {
    const deferred = Math.round(f.total * pct);
    const d1 = Math.round(deferred / 2);
    const d2 = deferred - d1;
    const inc: Record<number, number> = { ...roll[f.id] };
    inc[pinch] = f.total - deferred;
    if (next !== undefined) {
      inc[next] = (inc[next] ?? 0) + d1;
    }
    if (nextNext !== undefined) {
      inc[nextNext] = (inc[nextNext] ?? 0) + d2;
    }
    roll[f.id] = inc;
  }
  return roll;
}

/** The inverse of `applyDefer` — recovers the pacing percent an arbitrary roll plan implies
 *  (e.g. one the agent set directly), so the two directions round-trip exactly. */
export function deferPctFromRoll(roll: RollPlan, model: CapacityModel): number {
  const { pinch, families } = paceableFamilies(model);
  let total = 0;
  let deferred = 0;
  for (const f of families) {
    total += f.total;
    const actual = roll[f.id]?.[pinch] ?? f.total;
    deferred += Math.max(0, f.total - actual);
  }
  return total > 0 ? Math.round((deferred / total) * 100) : 0;
}

/** Faces deferred out of the pinch year, and where they land — what the capacity table's
 *  `−defer` / `+d1` / `+d2` cells and the pace lever card's delta both read. */
export function deferFigures(
  pacePct: number,
  model: CapacityModel,
): {
  pinch: number;
  defer: number;
  next?: { year: number; amount: number };
  after?: { year: number; amount: number };
} {
  const { pinch, families } = paceableFamilies(model);
  const total = families.reduce((a, f) => a + f.total, 0);
  const pct = Math.max(0, Math.min(100, pacePct)) / 100;
  const defer = Math.round(total * pct);
  const d1 = Math.round(defer / 2);
  const d2 = defer - d1;
  const next = CAP_YEARS[CAP_YEARS.indexOf(pinch) + 1];
  const nextNext = CAP_YEARS[CAP_YEARS.indexOf(pinch) + 2];
  return {
    pinch,
    defer,
    next: next !== undefined ? { year: next, amount: d1 } : undefined,
    after: nextNext !== undefined ? { year: nextNext, amount: d2 } : undefined,
  };
}

/* ===== the running position, one year at a time ===== */

export interface ScenarioResult {
  /** Real per-band totals, before any lever. */
  bandCounts: Record<Band, number>;
  /** What relocate/pack/convert each claimed, per band — sums to `claim.size`. */
  bandClaimed: Record<Band, { relo: number; pack: number; conv: number }>;
  /** Same fact the scatter dims by and the band chips read. */
  claim: Map<number, LeverCause>;
  relocate: number;
  pack: number;
  conv: number;
  /** relocate + pack + conv, de-duplicated (== `claim.size`). */
  released: number;
  /** Held per `CAP_YEARS` year, AFTER releases and pacing. 2026 is never touched by either. */
  held: Record<number, number>;
  /** Faces added by the capacity lever (0 if no system chosen). */
  addFaces: number;
  /** Real base ceiling plus `addFaces`. */
  ceiling: number;
  /** `held[year] - ceiling` for the CURRENT lever mix, i.e. negative = short. Keyed by year so
   *  callers don't have to re-run the model to check a different year. */
  roomByYear: Record<number, number>;
  /** Room for the working year (`params.year`) — the number the agent read talks about. */
  room: number;
  pctByYear: Record<number, number>;
  /** True the moment relocate reaches into the B band — the "escalate, don't force it"
   *  guardrail (build handover §4/§9). */
  bBandWarning: boolean;
  /** 2 once pack rationalisation reaches 6+ families (the two named waiver accounts); 0 otherwise. */
  waiverCount: number;
  /** True once conversion reaches past the 4th (uncertified) fitting set. */
  uncertifiedSetsEngaged: boolean;
}

export function runScenario(params: ScenarioParams, ctx: ScenarioContext): ScenarioResult {
  const { model, skus } = ctx;
  const bandCounts = realBandCounts(skus);
  const claim = buildClaimMap(params, ctx);
  const bandClaimed = bandClaimBreakdown(claim, skus);
  const removedFam = removedFamFor(params, ctx);
  const active = activeSpaceLevers(params);
  const reloStop = params.reloThreshold >= 0 ? reloStopForThreshold(params.reloThreshold) : 0;

  let relocate = 0;
  let pack = 0;
  let conv = 0;
  for (const cause of claim.values()) {
    if (cause === "relo") {
      relocate++;
    } else if (cause === "pack") {
      pack++;
    } else if (cause === "conv") {
      conv++;
    }
  }
  const released = claim.size;

  const addFaces = capacityAddFaces(params, ctx);
  const ceilBase = model.capd.ceil + addFaces;
  const roll = applyDefer(params.pacePct, model);
  const untouchedRoll = defaultRoll(model.npiFamilies);

  const held: Record<number, number> = {};
  const roomByYear: Record<number, number> = {};
  const pctByYear: Record<number, number> = {};
  const firstYear = CAP_YEARS[0];
  for (const year of CAP_YEARS) {
    if (year === firstYear) {
      // Baseline only — never touched by pacing or by any lever (build handover §5).
      held[year] = heldAt(untouchedRoll, year, model);
    } else {
      const n = capNumbers({
        year,
        roll,
        model,
        active,
        units: 0, // the capacity lever's ceiling math is handled above, off the real systems
        reloStop,
        families: ctx.families,
        removedFam,
        ceilBase,
      });
      held[year] = n.effHeld;
    }
    roomByYear[year] = ceilBase - held[year];
    pctByYear[year] = (held[year] / ceilBase) * 100;
  }

  return {
    bandCounts,
    bandClaimed,
    claim,
    relocate,
    pack,
    conv,
    released,
    held,
    addFaces,
    ceiling: ceilBase,
    roomByYear,
    room: roomByYear[params.year] ?? 0,
    pctByYear,
    bBandWarning: bandClaimed.B.relo > 0,
    waiverCount: params.packCount >= 6 ? 2 : 0,
    uncertifiedSetsEngaged: params.convCount > 4,
  };
}

/** Faces the capacity lever adds — `capUnits` × the chosen system's per-unit face count,
 *  scaled off the REAL ceiling (`ctx.systemAddFacesFraction`) so an edited ceiling still
 *  reconciles. 0 if no system is chosen. */
function capacityAddFaces(params: ScenarioParams, ctx: ScenarioContext): number {
  if (!params.capSystem || params.capUnits <= 0) {
    return 0;
  }
  const fraction = ctx.systemAddFacesFraction[params.capSystem];
  return Math.round(params.capUnits * fraction * ctx.model.capd.ceil);
}

/* ===== commercial helpers — real value, real accounts, real channel mix ===== */

/** £m of annual NTS sitting behind every SKU any lever claimed — real `Sku.value`, not the
 *  canon's engagement-scaled formula (`0.9·relocFrac + 1.1·packFrac + 0.4·convFrac`). */
export function atRiskValue(claim: ReadonlyMap<number, LeverCause>, skus: readonly Sku[]): number {
  let sum = 0;
  for (const s of skus) {
    if (claim.has(s.id)) {
      sum += s.value;
    }
  }
  return sum / 1_000_000;
}

/** Accounts touching a claimed SKU, summed off the real `Sku.accounts` count and capped at the
 *  site total — an upper-bound estimate (one account can carry several claimed SKUs), stated as
 *  such wherever it's shown, not a deduplicated account list (that needs an account-level feed
 *  this data doesn't carry). */
export function affectedAccounts(
  claim: ReadonlyMap<number, LeverCause>,
  skus: readonly Sku[],
  site: Site,
): number {
  let sum = 0;
  for (const s of skus) {
    if (claim.has(s.id)) {
      sum += s.accounts;
    }
  }
  return Math.min(site.accounts, Math.round(sum));
}

/** % of the portfolio's total NTS value sitting in SKUs relocate claimed — the real scaling
 *  factor the Channels view's mock PLDC/second-site story is interpolated against (pack/convert
 *  don't move site, so only `relo` counts here). */
export function channelShare(claim: ReadonlyMap<number, LeverCause>, skus: readonly Sku[]): number {
  let total = 0;
  let moved = 0;
  for (const s of skus) {
    total += s.value;
    if (claim.get(s.id) === "relo") {
      moved += s.value;
    }
  }
  return total > 0 ? (moved / total) * 100 : 0;
}

/** Monthly trade-sales curve (real `Sku.mon` × `Sku.value`) and, alongside it, the at-risk
 *  slice for the same month (claimed SKUs only) — what the Business view's bar chart draws. */
export function monthlyTradeSales(
  skus: readonly Sku[],
  claim: ReadonlyMap<number, LeverCause> | null,
): { total: number[]; atRisk: number[] } {
  const total = new Array(12).fill(0) as number[];
  const atRisk = new Array(12).fill(0) as number[];
  for (const s of skus) {
    const claimed = !!claim?.has(s.id);
    for (let i = 0; i < 12; i++) {
      const v = (s.mon[i] ?? 0) * s.value;
      total[i] = (total[i] ?? 0) + v;
      if (claimed) {
        atRisk[i] = (atRisk[i] ?? 0) + v;
      }
    }
  }
  return { total, atRisk };
}
